'use client';

import * as React from 'react';
import Link from 'next/link';
import { MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';

import { APP_NAME } from '@workspace/common/app';
import { Logo } from '@workspace/ui/components/logo';

// import { ThemeSwitcher } from '@workspace/ui/components/theme-switcher';

import { ExternalLink } from '~/components/fragments/external-link';
import { FOOTER_LINKS, SOCIAL_LINKS } from '~/components/marketing-links';

type ContactInfoProps = {
  icon: React.ElementType;
  text: string;
  href: string;
};

function ContactInfo({
  icon: Icon,
  text,
  href
}: ContactInfoProps): React.JSX.Element {
  return (
    <li>
      <Link
        href={href}
        title={text}
        target="_blank"
        rel="noopener noreferrer"
        className="text-muted-foreground hover:text-foreground relative flex flex-row items-start gap-2 text-sm transition-colors"
      >
        <Icon className="size-4 shrink-0" />
        <span>{text}</span>
      </Link>
    </li>
  );
}

export function Footer(): React.JSX.Element {
  return (
    <footer className="px-2 pb-10 pt-20 sm:container">
      <h2 className="sr-only">Footer</h2>
      <div className="container">
        <div className="xl:grid xl:grid-cols-6 xl:gap-8">
          <div className="hidden xl:block">
            <Logo />
            <p className="text-muted-foreground mt-3 text-xs">
              Luxe Rides oferă închiriere de mașini de lux și servicii decontate
              prin RCA pentru cei care apreciază excelența și rafinamentul.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4 lg:grid-cols-4 xl:col-span-5 xl:grid-cols-5">
            {FOOTER_LINKS.map((group) => (
              <div key={group.title}>
                <h3 className="text-foreground text-sm font-semibold">
                  {group.title}
                </h3>
                <ul
                  role="list"
                  className="mt-6 space-y-2"
                >
                  {group.links.map((link) => (
                    <li key={link.name}>
                      <Link
                        href={link.href}
                        title={link.name}
                        target={link.external ? '_blank' : undefined}
                        rel={link.external ? 'noopener noreferrer' : undefined}
                        className="text-muted-foreground hover:text-foreground relative flex items-center gap-2 text-sm transition-colors"
                      >
                        {link.name}
                        {link.external && (
                          <ExternalLink className="absolute right-[-10px] top-[2px] opacity-80" />
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <div className="lg:col-span-1 xl:col-span-2">
              <h3 className="text-foreground text-sm font-semibold">Contact</h3>
              <ul
                role="list"
                className="mt-6 space-y-2"
              >
                <ContactInfo
                  icon={PhoneIcon}
                  text="+40 799 562 479"
                  href="tel:+40799562479"
                />
                <ContactInfo
                  icon={MailIcon}
                  text="contact@luxerides.ro"
                  href="mailto:contact@luxerides.ro"
                />
                <ContactInfo
                  icon={MapPinIcon}
                  text="Șoseaua Andronache 66, București, Sector 2"
                  href="https://maps.google.com/maps?q=Șoseaua+Andronache+66,+București,+Sector+2"
                />
              </ul>
            </div>
          </div>
        </div>
        <div className="mt-8 border-t pt-8">
          <div className="flex flex-col items-center justify-between gap-4 xl:flex-row">
            <p className="text-muted-foreground text-sm">
              © {new Date().getFullYear()} {APP_NAME}. Toate drepturile
              rezervate.
            </p>
            <div className="flex flex-row items-center gap-4">
              {SOCIAL_LINKS.map((link) => (
                <Link
                  key={link.name}
                  title={link.name}
                  href={link.href}
                  className="text-muted-foreground hover:text-foreground relative flex items-center gap-2 transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="sr-only">{link.name}</span>
                  {link.icon}
                </Link>
              ))}
              {/* <Separator
                orientation="vertical"
                className="h-4"
              /> */}
              {/* <ThemeSwitcher /> */}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
